/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// SafeCircle Live Activity — the one place that ties native motion to RTDB.
//
// PRIVACY MODEL (do not loosen without product sign-off):
//  - OFF by default. We only START native updates + WRITE when the user has
//    explicitly enabled sharing (/users/{uid}/activitySharingEnabled) AND has
//    accepted the EULA AND is signed in.
//  - SafeCircle-only: the activity doc is gated by the safeCircleReaders index
//    in database.rules.json; this context just keeps that index reconciled
//    (backfill) once when sharing is active.
//  - Never streams: writes happen on a CHANGED normalized state, debounced to
//    >= 60s via shouldWriteActivity. On disable/sign-out we write a terminal
//    { sharingEnabled: false, state: 'unknown' } so watchers see it stop.

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { App as CapApp } from "@capacitor/app";
import { onValue, ref, serverTimestamp, set } from "firebase/database";
import { db } from "@/config/firebase";
import { driveTelemetryController } from "@/services/driveTelemetry";
import { useAuth } from "@/contexts/AuthContext";
import { useMotionActivity } from "@/hooks/useMotionActivity";
import { useSafeCircleStaticMembers } from "@/hooks/useSafeCircleStaticMembers";
import { requestAlwaysLocation } from "@/utils/locationAlways";
import {
  locationEscalationWarning,
  shouldWarnAboutLocation,
} from "@/utils/activity/locationEscalation";
import {
  type ActivityState,
  shouldWriteActivity,
} from "@/utils/activity/activityState";
import type { ActivityPermissionStatus } from "@/utils/motionActivity";
import { backfillSafeCircleReaders } from "@/utils/safecircle/safeCircleReaders";

interface ActivityContextValue {
  /** Whether the user has opted into sharing (persisted pref). */
  sharingEnabled: boolean;
  /** Toggle sharing on/off. Turning on triggers the permission flow; turning
   *  off stops updates and writes a terminal sharingEnabled:false doc. */
  setSharingEnabled: (enabled: boolean) => Promise<void>;
  /** Native motion permission status (for Settings copy / prompts). */
  permissionStatus: ActivityPermissionStatus;
  /** False on web / devices without a motion coprocessor. */
  isAvailable: boolean;
  /** Current own state (for optional self-display). */
  state: ActivityState;
}

const ActivityContext = createContext<ActivityContextValue | null>(null);

function activityDocRef(uid: string) {
  return ref(db, `users/${uid}/liveState/activity`);
}

export function ActivityProvider({ children }: { children: ReactNode }) {
  const { user, eulaAccepted } = useAuth();
  const {
    state,
    confidence,
    isAvailable,
    permissionStatus,
    requestPermission,
    start,
    stop,
  } = useMotionActivity();
  const { rawMembers } = useSafeCircleStaticMembers();

  const [sharingEnabled, setSharingEnabledState] = useState(false);

  // Debounce bookkeeping for the RTDB write gate.
  const lastWrittenStateRef = useRef<ActivityState | null>(null);
  const lastWriteAtRef = useRef<number | null>(null);
  const backfilledRef = useRef(false);
  // Whether driveTelemetry has been started at least once. On boot, `active`
  // is false until auth + sharingEnabled resolve, so the gate flickers
  // false→true. Calling driveTelemetryController.stop() on that initial false
  // reaches native disable() and (previously) killed a just-woken cold-wake
  // trip. We only call stop() AFTER having started, so the boot-time false is a
  // no-op. See DriveTelemetryService.disable().
  const driveTelemetryStartedRef = useRef(false);

  const uid = user?.uid ?? null;
  const active = Boolean(uid && eulaAccepted && sharingEnabled);

  // Subscribe to the persisted sharing preference.
  useEffect(() => {
    if (!uid) {
      setSharingEnabledState(false);
      return;
    }
    const prefRef = ref(db, `users/${uid}/activitySharingEnabled`);
    const unsub = onValue(prefRef, (snap) => {
      setSharingEnabledState(snap.val() === true);
    });
    return () => unsub();
  }, [uid]);

  // Start/stop native updates with the active gate.
  useEffect(() => {
    if (active) {
      void start();
    } else {
      void stop();
    }
    // start/stop are stable useCallback refs.
  }, [active, start, stop]);

  // DriveTelemetry Phase 1 — start/stop native driving-telemetry monitoring on
  // the SAME opt-in gate as live activity (one switch governs both). The
  // controller is iOS-only and no-ops elsewhere; it writes the rating to
  // /users/{uid}/liveState/driveRating only while sharing is on. Device-local
  // trip history is retained across stop() so re-enabling shows an accurate
  // rating immediately.
  useEffect(() => {
    if (active && uid) {
      driveTelemetryStartedRef.current = true;
      void driveTelemetryController.start(uid);
    } else if (driveTelemetryStartedRef.current) {
      // Only stop if we actually started — never on the boot-time gate flicker.
      driveTelemetryStartedRef.current = false;
      void driveTelemetryController.stop();
    }
  }, [active, uid]);

  // DriveTelemetry — drain queued trip summaries on every app FOREGROUND. Trips
  // usually end in the background while JS stays alive, so the gate effect above
  // (deps [active,uid]) does NOT re-fire when the app is brought forward — the
  // completed trip's summary would sit un-rated in the native queue until the
  // next cold start. An appStateChange listener flushes it whenever the app
  // becomes active. iOS-only; flush() no-ops without a uid / off-device.
  useEffect(() => {
    if (!active || !uid) return;
    let handle: { remove: () => void } | null = null;
    void CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void driveTelemetryController.flush();
    }).then((h) => {
      handle = h;
    });
    // Also flush once now (covers the case where we became active while a trip
    // had already ended and queued).
    void driveTelemetryController.flush();
    return () => {
      handle?.remove();
    };
  }, [active, uid]);

  // One-shot reader-index backfill once sharing is active, so members added
  // before this feature shipped become readable to the rule.
  useEffect(() => {
    if (!active || !uid || backfilledRef.current) return;
    backfilledRef.current = true;
    void backfillSafeCircleReaders(db, uid, rawMembers).catch((err) => {
      console.warn("[ActivityContext] reader backfill failed:", err);
      backfilledRef.current = false; // allow a retry next time
    });
  }, [active, uid, rawMembers]);

  // Reset debounce state whenever we deactivate so the next enable writes the
  // first sample immediately.
  useEffect(() => {
    if (!active) {
      lastWrittenStateRef.current = null;
      lastWriteAtRef.current = null;
      backfilledRef.current = false;
    }
  }, [active]);

  // Write on a debounced, changed state.
  useEffect(() => {
    if (!active || !uid) return;
    const now = Date.now();
    if (
      !shouldWriteActivity(
        lastWrittenStateRef.current,
        state,
        lastWriteAtRef.current,
        now,
      )
    ) {
      return;
    }
    lastWrittenStateRef.current = state;
    lastWriteAtRef.current = now;
    void set(activityDocRef(uid), {
      state,
      confidence,
      updatedAt: serverTimestamp(),
      sharingEnabled: true,
    }).catch((err) => {
      console.warn("[ActivityContext] activity write failed:", err);
    });
  }, [active, uid, state, confidence]);

  const setSharingEnabled = useCallback(
    async (enabled: boolean) => {
      if (!uid) return;
      if (enabled) {
        // Surface the system motion prompt before flipping the pref so the
        // user understands what they're enabling. We still persist the pref
        // even if permission is denied — the toggle reflects intent; the
        // native start() simply yields no updates until permission is granted.
        await requestPermission();
        // Escalate location to Always. DriveTelemetry's background trip capture
        // depends on it: CMMotionActivity's automotive classification FUSES with
        // location, and SLC / allowsBackgroundLocationUpdates only deliver under
        // Always authorization. On "While Using" the app gets no background
        // location while suspended, so a trip driven with the app closed is
        // never classified automotive → startTrip() never fires → the rating
        // freezes silently (the observed multi-week freeze). requestAlwaysLocation
        // is the same iOS-only bridge Walk Me Home uses; it no-ops on web/Android
        // and returns needsSettings when the user must change it manually. We do
        // NOT block the opt-in on the outcome — the pref reflects intent — but we
        // log a denial so the silent-freeze failure mode is diagnosable.
        const locOutcome = await requestAlwaysLocation();
        if (shouldWarnAboutLocation(locOutcome)) {
          console.warn(locationEscalationWarning(locOutcome));
        }
        await set(ref(db, `users/${uid}/activitySharingEnabled`), true);
      } else {
        await set(ref(db, `users/${uid}/activitySharingEnabled`), false);
        // Terminal doc so SafeCircle watchers see sharing stop immediately,
        // independent of the debounce/native teardown timing.
        await set(activityDocRef(uid), {
          state: "unknown",
          confidence: "low",
          updatedAt: serverTimestamp(),
          sharingEnabled: false,
        }).catch((err) => {
          console.warn("[ActivityContext] terminal write failed:", err);
        });
      }
    },
    [uid, requestPermission],
  );

  return (
    <ActivityContext.Provider
      value={{
        sharingEnabled,
        setSharingEnabled,
        permissionStatus,
        isAvailable,
        state,
      }}
    >
      {children}
    </ActivityContext.Provider>
  );
}

// Safe default for trees rendered without an ActivityProvider (e.g. the
// signed-out shell, or component tests that mount ProfileView in isolation).
// Sharing is reported off and the setter is a no-op, so Settings degrades
// gracefully rather than throwing.
const INERT_ACTIVITY: ActivityContextValue = {
  sharingEnabled: false,
  setSharingEnabled: async () => {},
  permissionStatus: "unavailable",
  isAvailable: false,
  state: "unknown",
};

export function useActivity(): ActivityContextValue {
  return useContext(ActivityContext) ?? INERT_ACTIVITY;
}
